import { useInfiniteQuery } from '@tanstack/react-query';
import { api, type ChatMentionFeedItemResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseChatMentionsResult {
  mentions: ChatMentionFeedItemResponse[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * The current user's cross-channel "Mentions" inbox — the most recent messages
 * that @mention them, newest-first, cursor-paginated. Read-only feed; clicking a
 * row jumps to the message. Only fetches while `enabled` (i.e. the panel is open)
 * so a closed surface doesn't run a background query.
 */
export function useChatMentions(enabled: boolean, pageSize = 30): UseChatMentionsResult {
  const query = useInfiniteQuery({
    queryKey: queryKeys.chat.mentions(),
    queryFn: ({ pageParam }) =>
      api.getChatMentions({ limit: pageSize, before: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
  });

  return {
    mentions: query.data?.pages.flatMap((p) => p.mentions) ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}
